import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Reusable button — lets the user upload a .txt/.docx/.pdf file
 * describing each scene, parses it server-side, and calls back with the
 * extracted per-scene prompts array.
 *
 *   <UploadScenePromptsButton
 *      testIdPrefix="ryo"
 *      onPrompts={(prompts, meta) => { ... }}
 *   />
 *
 * `onPrompts(prompts: string[], meta: { strategy, count, filename })`
 * fires after a successful parse. The component does NOT mutate any
 * state itself — parents decide where the prompts go.
 */
export default function UploadScenePromptsButton({
  testIdPrefix = "scene-prompts-upload",
  onPrompts,
  className = "",
  label = "Upload scene descriptions",
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const openPicker = () => {
    if (busy || disabled) return;
    if (inputRef.current) {
      inputRef.current.value = ""; // allow re-picking the same file
      inputRef.current.click();
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    if (!/\.(txt|md|docx|pdf)$/.test(name)) {
      toast.error("Unsupported file type — upload .txt, .docx, or .pdf");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/scene-prompts/parse", form);
      const { prompts = [], strategy, count, filename } = res.data || {};
      if (!Array.isArray(prompts) || prompts.length === 0) {
        toast.error("No scenes found in the document");
        return;
      }
      onPrompts?.(prompts, { strategy, count, filename });
      toast.success(
        `Loaded ${count} scene${count === 1 ? "" : "s"} from ${filename}`,
      );
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`${testIdPrefix}-button`}
        onClick={openPicker}
        disabled={busy || disabled}
        className={
          className
          || "font-mono text-[10px] uppercase tracking-[0.25em] border border-yellow-400/60 text-yellow-300 hover:bg-yellow-400/10 px-3 py-2 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        }
        title="Upload a .txt / .docx / .pdf describing each scene. Format scenes as: Scene 1: …, Scene 2: …"
      >
        {busy ? "Parsing…" : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".txt,.md,.docx,.pdf,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        data-testid={`${testIdPrefix}-input`}
        onChange={handleFile}
        className="hidden"
      />
    </>
  );
}
